import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { TextField, Button, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import _ from 'lodash';

// Images
import logo from '../../utils/images/TagLogo_AllWhite.png';

// Import css
import './Onboarding.scss';

// Import services
import { callOnBoardingApi } from './services';

// Import Zustand store
import { useAppStore } from '../../stores';

// Import components
import Dropdown from 'components/Dropdown/Dropdown';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import RecordConflictDialog from 'components/RecordConflictDialog/RecordConflictDialog';

// Import axios config
import { configureInterceptors } from '../../globalAxiosCall';

interface OnboardingRole {
  roleId: number;
  roleName: string;
  requiresMemberId: boolean;
  requiresAffiliatePin: boolean;
}

interface OnboardingPayload {
  uid?: number;
  affPinNum: string;
  memberId: string;
  RoleId?: number;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  isAffiliateStaff?: boolean;
  selectedRecord?: unknown;
}

const OnBoardingProcess: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const displayMobile = useMediaQuery('(max-width:959px)');

  // Zustand store
  const uid = useAppStore((state) => state.user.user) as { id?: number };
  const UACInfo = useAppStore((state) => state.user.UACTypedInInformation) as {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
  };
  const authenticated = useAppStore((state) => state.authentication.isLoginSuccess);
  const setUser = useAppStore((state) => state.setUser);
  const setActiveConvention = useAppStore((state) => state.setActiveConvention);
  const setAttendeeRecord = useAppStore((state) => state.setAttendeeRecord);
  const setUserRefreshToken = useAppStore((state) => state.setUserRefreshToken);
  const setUserJwtToken = useAppStore((state) => state.setUserJwtToken);
  const setUserPermissions = useAppStore((state) => state.setUserPermissions);
  const setLoginSuccess = useAppStore((state) => state.setLoginSuccess);
  const clearUserCreateDetails = useAppStore((state) => state.clearUserCreateDetails);

  const [formState, setFormState] = useState({
    affPinNum: '',
    memberId: '',
    credentialType: null as string | null,
  });

  const handleFormChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      [ev.target.name]: ev.target.value,
    });
  };

  const [onboardingRequestPending, setOnboardingRequestPending] = useState(false);
  const [chosenSelection, setChosenSelection] = useState('');
  const [roles, setRoles] = useState<OnboardingRole[]>([]);

  useEffect(() => {
    axios.get('/api/Security/onboardingRoles')
      .then((res) => setRoles(res.data))
      .catch(() => enqueueSnackbar('Failed to load roles', { variant: 'error' }));
  }, []);

  const submitOnBoarding = (payload: OnboardingPayload) => {
    const fullPayload: OnboardingPayload = {
      ...payload,
      email: UACInfo.email,
      password: UACInfo.password,
      firstName: UACInfo.firstName,
      lastName: UACInfo.lastName,
      isAffiliateStaff: roles.find(r => String(r.roleId) === chosenSelection)?.roleName === 'Affiliate Staff',
    };
    setOnboardingRequestPending(true);
    callOnBoardingApi(fullPayload)
      .then((response: { data: { refreshToken: string; jwtToken: string; userRecord: unknown; activeConvention: unknown; attendeeRecord: unknown; listOfPermissions: Record<string, unknown> } }) => {
        const { data } = response;
        const { refreshToken } = data;
        window.sessionStorage.setItem('loggedIn', 'true');
        setUser(data.userRecord as Record<string, unknown>);
        setActiveConvention(data.activeConvention);
        setAttendeeRecord(data.attendeeRecord);
        setUserRefreshToken(refreshToken);
        setUserJwtToken(data.jwtToken); // Store JWT token from response
        configureInterceptors(refreshToken);
        setUserPermissions(data.listOfPermissions);
        setOnboardingRequestPending(true);
      })
      .then(() => {
        setLoginSuccess();
        clearUserCreateDetails();
      })
      .catch(({ response }: { response?: { data?: { error?: { individualRecordConflict?: boolean; message?: string }; recordListing?: unknown[] } } }) => {
        setOnboardingRequestPending(false);
        if (_.get(response, 'data.error.individualRecordConflict')) {
          setConflictDialogState({
            ...conflictDialogState,
            records: _.get(response, 'data.recordListing') || [],
            selectedRecord: null,
            open: true,
          });
        } else {
          enqueueSnackbar(
            'Account creation error: ' +
              (_.get(response, 'data.error.message') || 'Unspecified error'),
            {
              variant: 'error',
            }
          );
        }
      });
  };

  const handleSubmitFormClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload: OnboardingPayload = {
      uid: uid?.id,
      affPinNum: formState.affPinNum,
      memberId: formState.memberId,
      RoleId: Number(chosenSelection) || 0,
    };
    submitOnBoarding(payload);
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement> | { target: { value: string } }) => {
    setChosenSelection(event.target.value);
  };

  // conflict resolution dialog handlers
  const [conflictDialogState, setConflictDialogState] = useState({
    records: [] as unknown[],
    open: false,
    selectedRecord: null as unknown,
  });

  const handleClose = () => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
    });
  };

  const handleListItemClick = (record: unknown) => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
      selectedRecord: record,
    });

    submitOnBoarding({
      affPinNum: formState.affPinNum,
      memberId: formState.memberId,
      selectedRecord: record,
    });
  };

  return authenticated ? (
    <Navigate to="/portal" replace />
  ) : (
    <div className="on-boarding-wrapper">
      {onboardingRequestPending && (
        <GenericSpinner includeOverlay={true} padding={25} />
      )}
      <div className="on-boarding-content" style={{ fontSize: '1.2rem' }}>
        <div className="text-center">
          <img
            src={logo}
            style={{
              width: '10em',
              height: '9em',
            }}
            alt="American Federation of Teachers Logo"
          />
        </div>
        <div className="pt-5 text-center">
          <span>
            Are you an AFT Member, Affiliate Staff, or Affiliate President?
          </span>
        </div>
        <div className="pt-3 text-center">
          <Dropdown
            id="onboarding-dropdown"
            label="Select a Role"
            selectedPropsValue={chosenSelection}
            required={false}
            width={displayMobile ? 100 : 50}
            values={[
              <option key="empty" value="">{''}</option>,
              ...roles.map((role) => (
                <option key={role.roleId} value={String(role.roleId)}>
                  {role.roleName}
                </option>
              ))
            ]}
            onChange={handleChange}
            name="credentialType"
          />
        </div>
        <form
          method="post"
          onSubmit={handleSubmitFormClick}
          className="login-form-onboarding text-center"
        >
          <div className="pt-3">
            {(() => {
              const selectedRole = roles.find(r => String(r.roleId) === chosenSelection);
              if (!selectedRole) return null;

              const showAffiliatePin = selectedRole.requiresAffiliatePin;
              const showMemberId = selectedRole.requiresMemberId;

              return (
                <div className="selection-wrapper">
                  <span>
                    {showAffiliatePin && showMemberId &&
                      'Please provide your Affiliate PIN & Member ID'}
                    {!showAffiliatePin && showMemberId &&
                      'Please provide a Member ID'}
                  </span>

                  <div>
                    {showAffiliatePin && (
                      <TextField
                        id="on-boarding-affiliate-number"
                        label="Affiliate PIN Number"
                        fullWidth={displayMobile}
                        type="text"
                        onChange={handleFormChange}
                        name="affPinNum"
                        autoComplete="affPinNum"
                        margin="normal"
                        variant="outlined"
                      />
                    )}

                    {showMemberId && (
                      <TextField
                        id="on-boarding-member-id"
                        label="Member ID"
                        type="text"
                        onChange={handleFormChange}
                        fullWidth={displayMobile}
                        style={{ paddingLeft: `${displayMobile ? '0' : '5'}px` }}
                        name="memberId"
                        autoComplete="memberId"
                        margin="normal"
                        variant="outlined"
                      />
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="pt-4 text-center">
              <Button
                type="submit"
                size="large"
                fullWidth={displayMobile}
                variant="contained"
                color="primary"
                disabled={!chosenSelection}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>

        <RecordConflictDialog
          handleClose={handleClose}
          handleListItemClick={handleListItemClick}
          open={conflictDialogState.open}
          records={conflictDialogState.records}
        />
      </div>
    </div>
  );
};

export default OnBoardingProcess;
